"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type MessageVolumeResponse } from "@/lib/api";

/**
 * 2026-04-29 — Stocktwits-style message volume gauge for the market
 * detail page (slice 3). Renders alongside MarketSentimentGauge above
 * the comments. Three-tier color band: low (muted) / medium (yellow) /
 * high (green) so a viewer can tell at a glance whether discussion is
 * active.
 *
 * Hidden silently when the API errors or the count is 0 — gauge is
 * decorative.
 */
export function MessageVolumeGauge({ marketId }: { marketId: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<MessageVolumeResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getMarketMessageVolume(marketId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        // silent — gauge is decorative
      });
    return () => {
      cancelled = true;
    };
  }, [marketId]);

  if (!data || data.totalCount === 0) return null;

  const tierTone =
    data.tier === "high"
      ? "var(--color-yes)"
      : data.tier === "medium"
        ? "var(--color-status-warning, #d97706)"
        : "var(--color-text3)";
  const tierLabel =
    data.tier === "high"
      ? t("messageVolume.tierHigh")
      : data.tier === "medium"
        ? t("messageVolume.tierMedium")
        : t("messageVolume.tierLow");

  return (
    <section
      className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2"
      aria-label={t("messageVolume.ariaLabel")}
    >
      <div>
        <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          {t("messageVolume.headingTemplate", { hours: data.windowHours })}
        </span>
        <p className="mt-0.5 text-[11px] text-[var(--color-text3)]">
          {data.rootCount}{" "}
          {data.rootCount === 1
            ? t("messageVolume.commentsOne")
            : t("messageVolume.commentsOther")}
          {" · "}
          {data.replyCount}{" "}
          {data.replyCount === 1
            ? t("messageVolume.repliesOne")
            : t("messageVolume.repliesOther")}
        </p>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span
          className="font-mono text-[20px] font-bold tabular-nums"
          style={{ color: tierTone }}
        >
          {data.totalCount}
        </span>
        <span
          className="text-[10px] font-bold uppercase tracking-[0.6px]"
          style={{ color: tierTone }}
        >
          {tierLabel}
        </span>
      </div>
    </section>
  );
}
